import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import theme from './theme';
import GlobalStyles from './globalStyles';
import './App.css';
import { CartProvider } from './contexts/CartContext';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Companies from './pages/Companies';
import Projects from './pages/Projects';
import Podcasts from './pages/Podcasts';
import Press from './pages/Press';
import Achievements from './pages/Achievements';
import Events from './pages/Events';
import Connect from './pages/Connect';
import Social from './pages/contact/Social';
import Schedule from './pages/Schedule';
import Store from './pages/Store';
import AllProducts from './pages/AllProducts';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './components/Checkout';
import Orders from './pages/Orders';
import OrderSuccess from './pages/OrderSuccess';
import Admin from './pages/Admin';
import ProductQuestions from './pages/ProductQuestions';
import { STORE_ENABLED } from './config/features';

// Components
import Navbar, { LanguageProvider } from './components/Navbar';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import ScrollToTop from './components/ScrollToTop';

// Mağaza rotaları — STORE_ENABLED false iken anasayfaya yönlendirir
const StoreDisabledRedirect = ({ home = '/' }: { home?: string }) => (
  <Navigate to={home} replace />
);

function getStoreRoutes(prefix = '') {
  const p = (route: string) => `${prefix}${route}`;
  const home = prefix || '/';

  if (!STORE_ENABLED) {
    return [
      <Route key={`${prefix}-store`} path={p('/store/*')} element={<StoreDisabledRedirect home={home} />} />,
      <Route key={`${prefix}-products`} path={p('/products')} element={<StoreDisabledRedirect home={home} />} />,
      <Route key={`${prefix}-cart`} path={p('/cart')} element={<StoreDisabledRedirect home={home} />} />,
      <Route key={`${prefix}-orders`} path={p('/orders')} element={<StoreDisabledRedirect home={home} />} />,
      <Route key={`${prefix}-order-success`} path={p('/order-success')} element={<StoreDisabledRedirect home={home} />} />,
    ];
  }

  return [
    <Route key={`${prefix}-store`} path={p('/store')} element={<Store />} />,
    <Route key={`${prefix}-products`} path={p('/products')} element={<AllProducts />} />,
    <Route key={`${prefix}-cart`} path={p('/cart')} element={<Cart />} />,
    <Route key={`${prefix}-order-success`} path={p('/order-success')} element={<OrderSuccess />} />,
    <Route key={`${prefix}-orders`} path={p('/orders')} element={<Orders />} />,
    <Route key={`${prefix}-checkout`} path={p('/store/checkout')} element={<Checkout />} />,
    <Route key={`${prefix}-product-cat`} path={p('/store/:category/:slug')} element={<ProductDetail />} />,
    <Route key={`${prefix}-product`} path={p('/store/:slug')} element={<ProductDetail />} />,
  ];
}

// Navbar wrapper component that checks location
const ConditionalNavbar = () => {
  const location = useLocation();
  const hideNavbarPaths = ['/cart', '/order-success'];
  const shouldHideNavbar = hideNavbarPaths.some(path => location.pathname.startsWith(path));
  
  return shouldHideNavbar ? null : <Navbar />;
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time - Runs only once on mount
    // console.log("Setting loading timer..."); // Debug log
    const timer = setTimeout(() => {
      // console.log("Timer finished, setting isLoading to false"); // Debug log
      setIsLoading(false);
    }, 2000); // Adjust time as needed (2000ms = 2 seconds)

    // Cleanup timer on unmount
    return () => clearTimeout(timer); 
  }, []); // Empty dependency array ensures this runs only once

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <Router>
          <ScrollToTop />
          <LanguageProvider>
            <CartProvider>
            <AnimatePresence mode="wait">
              {isLoading ? (
                <LoadingScreen key="loading" />
              ) : (
                <motion.div 
                  key="main-app"
                  className="App"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <ConditionalNavbar />
                  <Routes>
                    {/* English Routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/companies" element={<Companies />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/podcasts" element={<Podcasts />} />
                    <Route path="/press" element={<Press />} />
                    <Route path="/achievements" element={<Achievements />} />
                    <Route path="/events" element={<Events />} />
                    <Route path="/connect" element={<Connect />} />
                    <Route path="/schedule" element={<Schedule />} />
                    {getStoreRoutes()}
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/product-questions" element={<ProductQuestions />} />
                    <Route path="/connect/social" element={<Social />} />
                    
                    {/* Turkish Routes */}
                    <Route path="/tr" element={<Home />} />
                    <Route path="/tr/about" element={<About />} />
                    <Route path="/tr/companies" element={<Companies />} />
                    <Route path="/tr/projects" element={<Projects />} />
                    <Route path="/tr/podcasts" element={<Podcasts />} />
                    <Route path="/tr/press" element={<Press />} />
                    <Route path="/tr/achievements" element={<Achievements />} />
                    <Route path="/tr/events" element={<Events />} />
                    <Route path="/tr/connect" element={<Connect />} />
                    <Route path="/tr/schedule" element={<Schedule />} />
                    {getStoreRoutes('/tr')}
                    <Route path="/tr/connect/social" element={<Social />} />

                    {/* French Routes */}
                    <Route path="/fr" element={<Home />} />
                    <Route path="/fr/about" element={<About />} />
                    <Route path="/fr/companies" element={<Companies />} />
                    <Route path="/fr/projects" element={<Projects />} />
                    <Route path="/fr/podcasts" element={<Podcasts />} />
                    <Route path="/fr/press" element={<Press />} />
                    <Route path="/fr/achievements" element={<Achievements />} />
                    <Route path="/fr/events" element={<Events />} />
                    <Route path="/fr/connect" element={<Connect />} />
                    <Route path="/fr/schedule" element={<Schedule />} />
                    {getStoreRoutes('/fr')}
                    <Route path="/fr/connect/social" element={<Social />} />

                    {/* Spanish Routes */}
                    <Route path="/es" element={<Home />} />
                    <Route path="/es/about" element={<About />} />
                    <Route path="/es/companies" element={<Companies />} />
                    <Route path="/es/projects" element={<Projects />} />
                    <Route path="/es/podcasts" element={<Podcasts />} />
                    <Route path="/es/press" element={<Press />} />
                    <Route path="/es/achievements" element={<Achievements />} />
                    <Route path="/es/events" element={<Events />} />
                    <Route path="/es/connect" element={<Connect />} />
                    <Route path="/es/schedule" element={<Schedule />} />
                    {getStoreRoutes('/es')}
                    <Route path="/es/connect/social" element={<Social />} />

                    {/* Arabic Routes */}
                    <Route path="/ar" element={<Home />} />
                    <Route path="/ar/about" element={<About />} />
                    <Route path="/ar/companies" element={<Companies />} />
                    <Route path="/ar/projects" element={<Projects />} />
                    <Route path="/ar/podcasts" element={<Podcasts />} />
                    <Route path="/ar/press" element={<Press />} />
                    <Route path="/ar/achievements" element={<Achievements />} />
                    <Route path="/ar/events" element={<Events />} />
                    <Route path="/ar/connect" element={<Connect />} />
                    <Route path="/ar/schedule" element={<Schedule />} />
                    {getStoreRoutes('/ar')}
                    <Route path="/ar/connect/social" element={<Social />} />
                  </Routes>
                  <Footer />
                </motion.div>
              )}
            </AnimatePresence>
            </CartProvider>
          </LanguageProvider>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
